/**
 * Canonical Audit Trail Category Taxonomy
 * 
 * Provides deterministic, single-source-of-truth classification
 * of immutable audit records into authorized UI categories:
 * - clinical: Notes & Clinical
 * - supervision: Supervision
 * - esign: Guardian eSign
 * - billing: Billing & 837
 * - payroll: Payroll
 * - system: System
 * - uncategorized: Ambiguous or generic records (visible in All Events only)
 */

export type AuditCategory =
  | 'all'
  | 'clinical'
  | 'supervision'
  | 'esign'
  | 'billing'
  | 'payroll'
  | 'system'
  | 'uncategorized';

const CLINICAL_ACTIONS = new Set([
  'CREATE_CLINICAL_DOC',
  'CREATE_SOAP',
  'CREATE_NOTE',
  'SIGN_CLINICIAN',
  'CLINICIAN_SIGNED',
  'CREATE_CLIENT_NOTE',
  'DELETE_CLINICAL_DOC',
  'UPLOAD_DOCUMENT',
  'FULL_INTAKE_CREATED',
  'DELETE_CLIENT_GOAL',
  'UPDATE_CLIENT_CHART'
]);

const SUPERVISION_ACTIONS = new Set([
  'SIGN_SUPERVISOR',
  'SUPERVISOR_APPROVED',
  'RETURN_SUPERVISOR',
  'SUPERVISOR_RETURNED',
  'APPROVE_MANUAL_SOAP_EXCEPTION',
  'REVOKE_MANUAL_SOAP_EXCEPTION',
  'ASSIGN_CLIENT'
]);

const ESIGN_ACTIONS = new Set([
  'SEND_ESIGN',
  'DISPATCH_ESIGN',
  'SIGN_GUARDIAN',
  'GUARDIAN_SIGNED',
  'DISPATCH_UNIFIED_INTAKE_PACKET',
  'DISPATCH_INTAKE_PACKET',
  'INTAKE_WEBHOOK_COMPLETED',
  'VOID_ESIGN_ENVELOPE',
  'VOID_SUPERSEDED_ENVELOPE',
  'INTAKE_ENVELOPE_SUPERSEDED',
  'INTAKE_DISPATCH_FAILED',
  'PROCESS_GUARDIAN_REMINDERS',
  'ESIGN_RECONCILIATION',
  'INTAKE_DETAILS_UPDATED'
]);

const BILLING_ACTIONS = new Set([
  'EVALUATE_BILLING_READINESS',
  'BILLING_READY',
  'CREATE_CLAIM_837',
  'GENERATE_CLAIM_837',
  'SUBMIT_CLAIM_837',
  'BATCH_837_GENERATED'
]);

const PAYROLL_ACTIONS = new Set([
  'FINALIZE_PAYROLL',
  'UPDATE_PAYROLL_ADJUSTMENT'
]);

const SYSTEM_ACTIONS = new Set([
  'LOGIN_SUCCESS',
  'USER_LOGIN',
  'AUTH_LOGIN',
  'LOGOUT',
  'ROLE_SWITCH',
  'SWITCH_ROLE',
  'SWITCH_USER',
  'LOGIN_DENIED_ROSTER',
  'LOGIN_DENIED_DOMAIN',
  'LOGIN_FAILED',
  'WELLSKY_PRODUCTION_IMPORT'
]);

/**
 * Deterministically classifies an audit record into its canonical category.
 * If an action cannot be confidentially classified into one of the domain
 * categories, returns 'uncategorized' (visible under All Events).
 */
export function getCanonicalAuditCategory(action: string, _entityType?: string): AuditCategory {
  const normAction = (action || '').trim().toUpperCase();

  if (CLINICAL_ACTIONS.has(normAction)) {
    return 'clinical';
  }
  if (SUPERVISION_ACTIONS.has(normAction)) {
    return 'supervision';
  }
  if (ESIGN_ACTIONS.has(normAction)) {
    return 'esign';
  }
  if (BILLING_ACTIONS.has(normAction)) {
    return 'billing';
  }
  if (PAYROLL_ACTIONS.has(normAction)) {
    return 'payroll';
  }
  if (SYSTEM_ACTIONS.has(normAction)) {
    return 'system';
  }

  // Strictly avoid forcing ambiguous events into System
  return 'uncategorized';
}
